/**
 * Material field formatter
 */

import {useMix} from '@hconnect/tuc-processes/fields'
import type {MaterialVariations} from '@hconnect/tuc-processes/fields/mix/types'
import {useChangeableField} from '@hconnect/tuc-processes/orderManagement/orderChange'

import type {FieldFormatter, FieldFormatterResult} from './types'

/**
 * Formats mix variations (slump and setControl) into a display string
 * Format: "200 mm Slump | Sureset Bronze" or just one if only one is set
 */
const formatMixVariations = (variations: MaterialVariations | undefined | null): string | null => {
  if (!variations) return null

  const parts: string[] = []

  if (variations.slump?.description) {
    parts.push(`${variations.slump.description} Slump`)
  }

  if (variations.setControl?.description) {
    parts.push(variations.setControl.description)
  }

  return parts.length > 0 ? parts.join(' | ') : null
}

export const useMaterialFormatter = (): FieldFormatter => {
  const {
    selectedMaterialDisplay,
    defaultMaterial,
    selectedMaterialVariations,
    defaultMaterialVariations
  } = useMix()
  const materialState = useChangeableField('material')

  return (context) => {
    const {t} = context
    const requiresApproval = materialState.state === 'requireDispatchConfirmation'
    const hasChanges = materialState.isDirty

    const oldMaterialDesc = defaultMaterial?.materialDescription || null
    const newMaterialDesc = selectedMaterialDisplay?.materialDescription || null
    const oldCustomerDesc = defaultMaterial?.customerMaterialDescription || null
    const newCustomerDesc = selectedMaterialDisplay?.customerMaterialDescription || null

    const materialDescChanged = oldMaterialDesc !== newMaterialDesc
    const customerDescChanged = oldCustomerDesc !== newCustomerDesc

    const values: FieldFormatterResult['values'] = []

    // Add material description
    if (oldMaterialDesc && materialDescChanged) {
      values.push({text: oldMaterialDesc, isDiscarded: true, isNew: false})
    }
    if (newMaterialDesc) {
      values.push({text: newMaterialDesc, isDiscarded: false, isNew: materialDescChanged})
    }

    // Add customer material description on a new line
    if (oldCustomerDesc && customerDescChanged) {
      values.push({text: oldCustomerDesc, isDiscarded: true, isNew: false})
    }
    if (newCustomerDesc) {
      values.push({text: newCustomerDesc, isDiscarded: false, isNew: customerDescChanged})
    }

    // Add mix variations (slump | setControl) on a new line
    const oldVariationsText = formatMixVariations(
      defaultMaterialVariations as MaterialVariations | undefined
    )
    const newVariationsText = formatMixVariations(selectedMaterialVariations)
    const variationsChanged = oldVariationsText !== newVariationsText

    if (oldVariationsText && variationsChanged) {
      values.push({text: oldVariationsText, isDiscarded: true, isNew: false})
    }
    if (newVariationsText) {
      values.push({text: newVariationsText, isDiscarded: false, isNew: variationsChanged})
    }

    // If no values, show "Not specified"
    if (values.length === 0) {
      values.push({
        text: t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified'),
        isDiscarded: false,
        isNew: false
      })
    }

    return {values, requiresApproval, hasChanges}
  }
}
